import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, X } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import {
  useGetCandidatsSanctionQuery,
  useGetMessageDefautSanctionQuery,
  useAppliquerSanctionMutation,
  useRetirerSanctionMutation,
  useVisibiliteRattrapageMutation,
  CandidatSanction,
  Parite,
} from '../../services/sanctionsApi';

const PAR_PAGE = 100;

type Statut = 'tous' | 'sanctionnes' | 'non_sanctionnes';
type Modale = null | 'appliquer' | 'retirer' | 'afficher' | 'masquer';

const SELECT_CLS =
  'w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 ' +
  'text-sm text-gray-900 dark:text-gray-100';
const LABEL_CLS = 'block text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1';
const BTN_SECONDAIRE =
  'px-3 py-2 rounded text-sm font-medium border border-gray-300 dark:border-gray-600 ' +
  'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 ' +
  'disabled:opacity-40 disabled:cursor-not-allowed';
const BTN_DANGER =
  'px-3 py-2 rounded text-sm font-semibold bg-red-700 hover:bg-red-800 text-white ' +
  'disabled:opacity-40 disabled:cursor-not-allowed';

const uniques = (valeurs: (string | null | undefined)[]): string[] =>
  Array.from(new Set(valeurs.filter((v): v is string => !!v))).sort((a, b) => a.localeCompare(b, 'fr'));

const fmtDate = (iso?: string | null): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

const erreurApi = (e: any): string =>
  e?.data?.detail || (typeof e?.error === 'string' ? e.error : '') || 'Une erreur est survenue.';

const nomComplet = (e: CandidatSanction) => `${e.nom || ''} ${e.prenoms || ''}`.trim();

export const AnnulationsSession: React.FC = () => {
  const [parite, setParite] = useState<Parite>('S2');
  const [pole, setPole] = useState('');
  const [niveau, setNiveau] = useState('');
  const [classe, setClasse] = useState('');
  const [option, setOption] = useState('');
  const [typeFormation, setTypeFormation] = useState('');
  const [statut, setStatut] = useState<Statut>('tous');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [modale, setModale] = useState<Modale>(null);
  const [flash, setFlash] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);

  const [motif, setMotif] = useState('fuite_sujets');
  const [reference, setReference] = useState('');
  const [details, setDetails] = useState('');
  const [message, setMessage] = useState('');
  const [confirme, setConfirme] = useState(false);
  const [motifRetrait, setMotifRetrait] = useState('');

  const { data, isLoading, isFetching, refetch } = useGetCandidatsSanctionQuery({ semestre: parite });
  const { data: msgDefaut } = useGetMessageDefautSanctionQuery({ semestre: parite });
  const [appliquer, { isLoading: enApplication }] = useAppliquerSanctionMutation();
  const [retirer, { isLoading: enRetrait }] = useRetirerSanctionMutation();
  const [visibilite, { isLoading: enVisibilite }] = useVisibiliteRattrapageMutation();

  useEffect(() => {
    setSelection(new Set());
    setPage(1);
    setMessage('');
  }, [parite]);

  useEffect(() => {
    setPage(1);
  }, [pole, niveau, classe, option, typeFormation, statut, recherche]);

  const tous = useMemo(() => data?.results ?? [], [data]);

  const poles = useMemo(() => uniques(tous.map((e) => e.pole)), [tous]);
  const niveaux = useMemo(() => uniques(tous.map((e) => e.niveau)), [tous]);
  const types = useMemo(() => uniques(tous.map((e) => e.type_formation)), [tous]);
  const perimetre = useMemo(
    () => tous.filter((e) => (!pole || e.pole === pole) && (!niveau || e.niveau === niveau)),
    [tous, pole, niveau],
  );
  const classes = useMemo(() => uniques(perimetre.map((e) => e.classe)), [perimetre]);
  const options = useMemo(() => uniques(perimetre.map((e) => e.option)), [perimetre]);

  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return tous.filter((e) => {
      if (pole && e.pole !== pole) return false;
      if (niveau && e.niveau !== niveau) return false;
      if (classe && e.classe !== classe) return false;
      if (option && e.option !== option) return false;
      if (typeFormation && e.type_formation !== typeFormation) return false;
      if (statut === 'sanctionnes' && !e.sanction) return false;
      if (statut === 'non_sanctionnes' && e.sanction) return false;
      if (q && !`${e.matricule} ${e.nom} ${e.prenoms}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tous, pole, niveau, classe, option, typeFormation, statut, recherche]);

  const nbPages = Math.max(1, Math.ceil(filtres.length / PAR_PAGE));
  const pageCourante = Math.min(page, nbPages);
  const visibles = filtres.slice((pageCourante - 1) * PAR_PAGE, pageCourante * PAR_PAGE);

  const selectionnes = useMemo(() => tous.filter((e) => selection.has(e.matricule)), [tous, selection]);
  const aSanctionner = selectionnes.filter((e) => !e.sanction);
  const dejaSanctionnes = selectionnes.filter((e) => !!e.sanction);
  const idsSanctions = dejaSanctionnes.map((e) => (e.sanction ? e.sanction.id : 0)).filter((id) => id > 0);

  const libelleParite = parite === 'S1' ? 'impairs (S1/S3/S5)' : 'pairs (S2/S4/S6)';

  const basculer = (matricule: string) =>
    setSelection((prev) => {
      const suivant = new Set(prev);
      if (suivant.has(matricule)) suivant.delete(matricule);
      else suivant.add(matricule);
      return suivant;
    });

  const pageEntiereCochee = visibles.length > 0 && visibles.every((e) => selection.has(e.matricule));

  const basculerPage = () =>
    setSelection((prev) => {
      const suivant = new Set(prev);
      if (pageEntiereCochee) visibles.forEach((e) => suivant.delete(e.matricule));
      else visibles.forEach((e) => suivant.add(e.matricule));
      return suivant;
    });

  const cocherFiltre = () =>
    setSelection((prev) => {
      const suivant = new Set(prev);
      filtres.forEach((e) => suivant.add(e.matricule));
      return suivant;
    });

  const ouvrir = (m: Modale) => {
    setConfirme(false);
    if (m === 'appliquer' && !message) setMessage(msgDefaut?.message || '');
    if (m === 'retirer') setMotifRetrait('');
    setModale(m);
  };

  const fermer = () => setModale(null);

  const lancerAppliquer = async () => {
    try {
      const r = await appliquer({
        matricules: aSanctionner.map((e) => e.matricule),
        semestre: parite,
        motif,
        details,
        reference_decision: reference,
        message_portail: message,
      }).unwrap();
      setFlash({ type: 'ok', texte: r.message });
      setSelection(new Set());
      setReference('');
      setDetails('');
      setModale(null);
    } catch (e) {
      setFlash({ type: 'erreur', texte: erreurApi(e) });
    }
  };

  const lancerRetrait = async () => {
    try {
      const r = await retirer({ ids: idsSanctions, motif_retrait: motifRetrait }).unwrap();
      setFlash({ type: 'ok', texte: r.message });
      setSelection(new Set());
      setModale(null);
    } catch (e) {
      setFlash({ type: 'erreur', texte: erreurApi(e) });
    }
  };

  const lancerVisibilite = async (visible: boolean) => {
    try {
      const r = await visibilite({ ids: idsSanctions, visible }).unwrap();
      setFlash({ type: 'ok', texte: r.message });
      setSelection(new Set());
      setModale(null);
    } catch (e) {
      setFlash({ type: 'erreur', texte: erreurApi(e) });
    }
  };

  const peutAppliquer =
    confirme && (reference.trim() !== '' || details.trim() !== '') && aSanctionner.length > 0 && !enApplication;

  const apercuNoms = (liste: CandidatSanction[]) => {
    const noms = liste.slice(0, 8).map((e) => `${nomComplet(e)} (${e.classe})`);
    const reste = liste.length - noms.length;
    return reste > 0 ? `${noms.join(', ')} et ${reste} autre(s)` : noms.join(', ');
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Annulation de session</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
            Sanction disciplinaire décidée par la Direction. La session ordinaire du semestre choisi est annulée
            pour les étudiants sélectionnés : leurs notes d'examen de cette session ne comptent plus, leurs notes
            de devoirs sont conservées et ils sont inscrits au rattrapage. Sur le portail, ils voient un message
            à la place de leurs résultats.
          </p>
        </div>
        <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} /> Actualiser
        </Button>
      </div>

      {flash && (
        <div
          className={`flex items-start justify-between gap-3 rounded border px-4 py-3 text-sm ${
            flash.type === 'ok'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
              : 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200'
          }`}
        >
          <span className="whitespace-pre-wrap">{flash.texte}</span>
          <button onClick={() => setFlash(null)} aria-label="Fermer" className="opacity-60 hover:opacity-100">
            <X size={16} />
          </button>
        </div>
      )}

      <Card>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Semestre concerné</label>
            <select value={parite} onChange={(e) => setParite(e.target.value as Parite)} className={SELECT_CLS}>
              <option value="S2">Semestres pairs (S2 / S4 / S6)</option>
              <option value="S1">Semestres impairs (S1 / S3 / S5)</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Statut</label>
            <select value={statut} onChange={(e) => setStatut(e.target.value as Statut)} className={SELECT_CLS}>
              <option value="tous">Tous les étudiants</option>
              <option value="sanctionnes">Session annulée</option>
              <option value="non_sanctionnes">Non sanctionnés</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Pôle</label>
            <select
              value={pole}
              onChange={(e) => { setPole(e.target.value); setClasse(''); setOption(''); }}
              className={SELECT_CLS}
            >
              <option value="">Tous</option>
              {poles.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Niveau</label>
            <select
              value={niveau}
              onChange={(e) => { setNiveau(e.target.value); setClasse(''); setOption(''); }}
              className={SELECT_CLS}
            >
              <option value="">Tous</option>
              {niveaux.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Classe</label>
            <select value={classe} onChange={(e) => setClasse(e.target.value)} className={SELECT_CLS}>
              <option value="">Toutes</option>
              {classes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Option</label>
            <select value={option} onChange={(e) => setOption(e.target.value)} className={SELECT_CLS}>
              <option value="">Toutes</option>
              {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Type de formation</label>
            <select value={typeFormation} onChange={(e) => setTypeFormation(e.target.value)} className={SELECT_CLS}>
              <option value="">Tous</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Recherche</label>
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Matricule, nom ou prénom"
                className="pl-8 text-sm"
              />
            </div>
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-600 dark:text-gray-300 mr-2">
          <b className="tabular-nums">{filtres.length}</b> étudiant(s) affiché(s) ·{' '}
          <b className="tabular-nums">{data?.nb_sanctionnes ?? 0}</b> session(s) annulée(s) ·{' '}
          <b className="tabular-nums">{selection.size}</b> sélectionné(s)
        </span>
        <button className={BTN_SECONDAIRE} onClick={cocherFiltre} disabled={filtres.length === 0}>
          Sélectionner le filtre
        </button>
        <button className={BTN_SECONDAIRE} onClick={() => setSelection(new Set())} disabled={selection.size === 0}>
          Vider la sélection
        </button>
        <div className="flex-1" />
        <button className={BTN_DANGER} onClick={() => ouvrir('appliquer')} disabled={aSanctionner.length === 0}>
          Annuler la session ({aSanctionner.length})
        </button>
        <button className={BTN_SECONDAIRE} onClick={() => ouvrir('afficher')} disabled={idsSanctions.length === 0}>
          Afficher résultats rattrapage ({idsSanctions.length})
        </button>
        <button className={BTN_SECONDAIRE} onClick={() => ouvrir('masquer')} disabled={idsSanctions.length === 0}>
          Masquer résultats ({idsSanctions.length})
        </button>
        <button className={BTN_SECONDAIRE} onClick={() => ouvrir('retirer')} disabled={idsSanctions.length === 0}>
          Retirer la sanction ({idsSanctions.length})
        </button>
      </div>

      <Card>
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : filtres.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Aucun étudiant ne correspond à ces filtres.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60">
                  <th className="px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={pageEntiereCochee}
                      onChange={basculerPage}
                      aria-label="Cocher toute la page"
                    />
                  </th>
                  <th className="px-3 py-2 font-semibold">Matricule</th>
                  <th className="px-3 py-2 font-semibold">Nom et prénoms</th>
                  <th className="px-3 py-2 font-semibold">Classe</th>
                  <th className="px-3 py-2 font-semibold">Pôle</th>
                  <th className="px-3 py-2 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((e) => {
                  const coche = selection.has(e.matricule);
                  return (
                    <tr
                      key={e.matricule}
                      onClick={() => basculer(e.matricule)}
                      className={`border-t border-gray-100 dark:border-gray-800 cursor-pointer ${
                        coche ? 'bg-red-50/60 dark:bg-red-950/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
                      }`}
                    >
                      <td className="px-3 py-2" onClick={(ev) => ev.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={coche}
                          onChange={() => basculer(e.matricule)}
                          aria-label={`Sélectionner ${nomComplet(e)}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{e.matricule}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{nomComplet(e)}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{e.classe}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{e.pole}</td>
                      <td className="px-3 py-2">
                        {e.sanction ? (
                          <div>
                            <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                              Session annulée
                            </span>
                            {e.sanction.resultats_rattrapage_visibles && (
                              <span className="ml-1 inline-block text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300">
                                Résultats rattrapage visibles
                              </span>
                            )}
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                              {e.sanction.reference_decision ? `Réf. ${e.sanction.reference_decision} · ` : ''}
                              {fmtDate(e.sanction.date_creation)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {nbPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-800 text-sm">
            <span className="text-gray-500">Page {pageCourante} / {nbPages}</span>
            <div className="flex gap-2">
              <button className={BTN_SECONDAIRE} disabled={pageCourante <= 1} onClick={() => setPage(pageCourante - 1)}>
                Précédente
              </button>
              <button className={BTN_SECONDAIRE} disabled={pageCourante >= nbPages} onClick={() => setPage(pageCourante + 1)}>
                Suivante
              </button>
            </div>
          </div>
        )}
      </Card>

      {modale && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={fermer}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white dark:bg-gray-900 shadow-xl"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {modale === 'appliquer' && `Annuler la session ordinaire — semestres ${libelleParite}`}
                {modale === 'retirer' && 'Retirer la sanction'}
                {modale === 'afficher' && 'Afficher les résultats de rattrapage'}
                {modale === 'masquer' && 'Masquer les résultats et réafficher le message'}
              </h2>
              <button onClick={fermer} aria-label="Fermer" className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X size={18} />
              </button>
            </div>

            {modale === 'appliquer' && (
              <div className="px-5 py-4 space-y-4 text-sm">
                <p className="text-gray-700 dark:text-gray-300">
                  <b>{aSanctionner.length}</b> étudiant(s) : {apercuNoms(aSanctionner)}.
                </p>
                <div className="rounded border border-red-300 bg-red-50 px-4 py-3 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
                  <p className="font-semibold mb-1">Effets de la sanction</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    <li>Les notes d'examen de la session ordinaire ne sont plus retenues ; elles restent conservées en base.</li>
                    <li>Les notes de devoirs sont conservées et compteront avec le rattrapage.</li>
                    <li>Les résultats du semestre sont recalculés : les étudiants passent « non validé, doit rattraper » et entrent dans la chaîne du rattrapage.</li>
                    <li>Une fois les résultats publiés, le portail étudiant et le portail parent affichent le message ci-dessous à la place des résultats.</li>
                  </ul>
                </div>
                {parite === 'S1' && (
                  <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                    Les semestres impairs sont déjà publiés : les résultats publiés de ces étudiants seront modifiés.
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLS}>Motif</label>
                    <select value={motif} onChange={(e) => setMotif(e.target.value)} className={SELECT_CLS}>
                      <option value="fuite_sujets">Implication dans la fuite des sujets d'examen</option>
                      <option value="autre">Autre motif disciplinaire</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Référence de la décision</label>
                    <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ex. Décision DIR-2026-014" />
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLS}>Motif détaillé (interne)</label>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    rows={3}
                    className={SELECT_CLS}
                    placeholder="Non visible par l'étudiant."
                  />
                </div>
                <div>
                  <label className={LABEL_CLS}>Message affiché sur le portail</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    className={SELECT_CLS}
                  />
                  <p className="mt-1 text-[11px] text-gray-500">Visible par l'étudiant et par ses parents.</p>
                </div>
                <label className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} className="mt-0.5" />
                  <span>Je confirme appliquer la décision de la Direction à ces {aSanctionner.length} étudiant(s).</span>
                </label>
                {!reference.trim() && !details.trim() && (
                  <p className="text-[12px] text-amber-700 dark:text-amber-300">
                    Renseignez la référence de la décision ou un motif détaillé.
                  </p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button className={BTN_SECONDAIRE} onClick={fermer}>Annuler</button>
                  <button className={BTN_DANGER} disabled={!peutAppliquer} onClick={lancerAppliquer}>
                    {enApplication ? 'Application et recalcul…' : 'Appliquer la sanction'}
                  </button>
                </div>
              </div>
            )}

            {modale === 'retirer' && (
              <div className="px-5 py-4 space-y-4 text-sm">
                <p className="text-gray-700 dark:text-gray-300">
                  <b>{dejaSanctionnes.length}</b> étudiant(s) : {apercuNoms(dejaSanctionnes)}.
                </p>
                <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  Les notes d'examen de la session ordinaire seront de nouveau prises en compte et les résultats
                  recalculés. À réserver à une erreur de sélection ou à une décision de levée de la sanction.
                </div>
                <div>
                  <label className={LABEL_CLS}>Motif du retrait</label>
                  <textarea
                    value={motifRetrait}
                    onChange={(e) => setMotifRetrait(e.target.value)}
                    rows={3}
                    className={SELECT_CLS}
                    placeholder="Au moins 10 caractères."
                  />
                </div>
                <label className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} className="mt-0.5" />
                  <span>Je confirme le retrait de la sanction pour ces étudiants.</span>
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <button className={BTN_SECONDAIRE} onClick={fermer}>Annuler</button>
                  <button
                    className={BTN_DANGER}
                    disabled={!confirme || motifRetrait.trim().length < 10 || enRetrait}
                    onClick={lancerRetrait}
                  >
                    {enRetrait ? 'Retrait et recalcul…' : 'Retirer la sanction'}
                  </button>
                </div>
              </div>
            )}

            {(modale === 'afficher' || modale === 'masquer') && (
              <div className="px-5 py-4 space-y-4 text-sm">
                <p className="text-gray-700 dark:text-gray-300">
                  <b>{dejaSanctionnes.length}</b> étudiant(s) : {apercuNoms(dejaSanctionnes)}.
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  {modale === 'afficher'
                    ? "Le portail cessera d'afficher le message de sanction et montrera les résultats du semestre, calculés sur les devoirs et le rattrapage uniquement. À utiliser une fois les résultats de rattrapage délibérés."
                    : "Le portail affichera de nouveau le message de sanction à la place des résultats."}
                </p>
                <label className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} className="mt-0.5" />
                  <span>Je confirme.</span>
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <button className={BTN_SECONDAIRE} onClick={fermer}>Annuler</button>
                  <button
                    className={modale === 'afficher' ? BTN_DANGER : BTN_SECONDAIRE}
                    disabled={!confirme || enVisibilite}
                    onClick={() => lancerVisibilite(modale === 'afficher')}
                  >
                    {modale === 'afficher' ? 'Afficher les résultats' : 'Réafficher le message'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnulationsSession;
